import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import FlexSearch from "flexsearch";
import { useStaticQuery, graphql, Link } from "gatsby";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import Image from "./image";
import { checkDarkMode, modifyColor } from "../utils/helpers";

const Wrapper = styled(({ searchOpen: ignored, ...rest }) => <div {...rest} />)`
  width: 100%;
  display: flex;
  position: relative;
  justify-content: flex-end;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
    flex-direction: column;
    transition: all 0.3s ease-in-out;
    margin: 0;
    transform: ${({ searchOpen }) =>
      searchOpen ? "translateY(0)" : "translateY(-100%)"};
    position: fixed;
    top: 0;
    height: 100vh;
    padding: 0 20px;
    background-color: ${({ theme }) => theme.colors.secondary};
    z-index: 999;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0 10px;
  }
`;
const SearchBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-right: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-top: 1.5rem;
    width: 100%;
    margin-right: 0;
  }
`;
const SearchInput = styled.input`
  padding: 15px 15px;
  width: 450px;
  box-shadow: none;
  border: 1px solid
    ${({ theme }) => theme.colors.headerSearch || theme.colors.primary};
  &:focus {
    outline: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 50px;
    width: 100%;
  }
`;
const SearchButton = styled.button`
  background-color: ${({ theme }) =>
    theme.colors.headerSearch || theme.colors.primary};
  padding: 15px;
  border: none;
  border-right: 1px solid
    ${({ theme }) => theme.colors.headerSearch || theme.colors.primary};
  margin-left: -10px;
  cursor: pointer;
  transition: all 0.5s ease;
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) =>
        modifyColor(theme.colors.headerSearch || theme.colors.primary, -40)};
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 50px;
  }
`;
const SearchIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.white};
`;
const SearchResults = styled.div`
  position: absolute;
  top: 49px;
  right: 32px;
  width: 490px;
  background-color: ${({ theme }) => checkDarkMode("#fff", theme)};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  max-height: 550px;
  overflow-y: scroll;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: relative;
    width: 100%;
    top: 0;
    right: 0;
    background-color: transparent;
    border: none;
  }
`;

const Result = styled(Link)`
  display: flex;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  transition: all 0.5 ease;
  color: ${({ theme }) => theme.colors.black};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: ${checkDarkMode("#efefef")};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 1.3rem;
  }
`;

const SearchImage = styled(Image)`
  margin-right: 15px;
  max-height: 35px;
  min-width: 50px;
`;

const Content = styled.div`
  font-size: 0.9rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 1rem;
  }
`;

const MobileClose = styled.div`
  margin: 0 auto;
  font-size: 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: 5px 10px;
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Search = ({ staticSearchData, searchOpen, onClose }) => {
  const { localSearchPages } =
    staticSearchData ||
    useStaticQuery(graphql`
      query SearchsearchQuery {
        localSearchPages {
          publicIndexURL
          publicStoreURL
        }
      }
    `);

  const [searchIndex, setSearchIndex] = useState(null);
  const [searchStore, setSearchStore] = useState(null);
  const [shouldLoadSearch, loadSearch] = React.useReducer(() => true, false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rawResults, setRawResults] = useState(null);
  const [results, setResults] = useState(null);
  const [flexSearch, setFlexSearch] = useState(null);

  useEffect(() => {
    // Load search index
    if (shouldLoadSearch && !searchIndex) {
      fetch(localSearchPages.publicIndexURL)
        .then((response) => response.text())
        .then((index) => setSearchIndex(index));
    }
  }, [shouldLoadSearch, searchIndex]);

  useEffect(() => {
    // Load search store
    if (shouldLoadSearch && !searchStore) {
      fetch(localSearchPages.publicStoreURL)
        .then((response) => response.json())
        .then((store) => setSearchStore(store));
    }
  }, [shouldLoadSearch, searchStore]);

  useEffect(() => {
    // Create search object
    if (!flexSearch && searchIndex) {
      const newFlexSearch = FlexSearch.create();
      newFlexSearch.import(searchIndex);
      setFlexSearch(newFlexSearch);
    }
  }, [flexSearch, searchIndex]);

  useEffect(() => {
    // Do the actual search
    if (flexSearch && searchStore) {
      setRawResults(
        flexSearch.search(searchQuery).map((id) => searchStore[id])
      );
    }
  }, [searchQuery, flexSearch, searchStore]);

  useEffect(() => {
    // Process the search results returned by FlexSearch, highlight matches etc
    const sentenceRegex = new RegExp(
      "[^.,]*(" +
        searchQuery
          ?.split(/\s+/)
          .filter((i) => i?.length)
          .join("|") +
        ")[^.,]*",
      "gi"
    );

    const regex = new RegExp(
      searchQuery
        ?.split(/\s+/)
        .filter((i) => i?.length)
        .join("|"),
      "gi"
    );

    setResults(
      rawResults?.map((result) => {
        const matches = [...(result.content?.matchAll(sentenceRegex) || null)];
        return {
          title: result.title,
          id: result.id,
          uri: result.uri,
          matches: matches.map(([sentence]) =>
            sentence.replace(regex, (match) => `<strong>${match}</strong>`)
          ),
          image: result.image,
        };
      })
    );
  }, [rawResults]);

  const doSearch = (e) => {
    let onlyLetters = /^[-A-Za-z0-9\u00C0-\u00ff ]+$/.test(e);
    if (e.length >= 2 && onlyLetters) {
      setSearchQuery(e);
    } else {
      setSearchQuery("");
    }
  };

  return (
    <Wrapper searchOpen={searchOpen}>
      <MobileClose onClick={() => onClose()}>
        <FontAwesomeIcon icon={faTimes} />
      </MobileClose>
      <SearchBar>
        <SearchInput
          type="text"
          onChange={(event) => doSearch(event.target.value)}
          onFocus={() => {
            loadSearch();
          }}
        />
        <SearchButton>
          <SearchIcon icon={faSearch} size="lg" />
        </SearchButton>
      </SearchBar>
      {results?.length > 0 && (
        <SearchResults>
          {results.map((result) => (
            <Result
              key={result.id}
              style={{ flexDirection: result.image ? "row" : "column" }}
              to={result.uri}
            >
              {result.image && <SearchImage image={result.image} />}
              <div>
                <h4>{result.title}</h4>
                {result.matches.map((match, i) => (
                  <Content
                    key={i}
                    dangerouslySetInnerHTML={{ __html: match }}
                  ></Content>
                ))}
              </div>
            </Result>
          ))}
        </SearchResults>
      )}
    </Wrapper>
  );
};

Search.propTypes = {
  searchOpen: PropTypes.bool,
  onClose: PropTypes.func,
  staticSearchData: PropTypes.object,
};

export default Search;
